import { useState } from "react";
import { useSnackbar } from "notistack";
import { useFunctionLogs } from "../../hooks/useFunctionLogs";
import { useIcps } from "../../hooks/useIcps";
import { useContactResearch } from "../../hooks/useMutations";
import { errorPayload, statusFromMutation } from "../../lib/mutationStatus";
import { CampaignInput } from "../CampaignInput";
import { ContactResearchResult } from "../results/ContactResearchResult";
import { JsonResult } from "../results/JsonResult";
import { LogStream } from "../LogStream";
import { StatusLine } from "../StatusLine";

export function ContactResearchCard() {
  const { enqueueSnackbar } = useSnackbar();
  const { data: icps } = useIcps();
  const mutation = useContactResearch();

  const [maxContacts, setMaxContacts] = useState(50);
  const [icpName, setIcpName] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [ignoreOtherCampaigns, setIgnoreOtherCampaigns] = useState(false);
  const [ignorePipedriveDedup, setIgnorePipedriveDedup] = useState(false);
  const [runStartTime, setRunStartTime] = useState<Date | null>(null);
  const logs = useFunctionLogs(
    "cf_contact_research",
    runStartTime,
    mutation.isPending,
  );

  const selectedIcp = icpName || icps?.[0]?.name || "";

  const onRun = () => {
    if (!maxContacts || maxContacts < 1) {
      enqueueSnackbar("Max contacts must be > 0.", { variant: "warning" });
      return;
    }
    if (!selectedIcp) {
      enqueueSnackbar("ICP name is required.", { variant: "warning" });
      return;
    }
    if (!campaignId.startsWith("cam_")) {
      enqueueSnackbar("Campaign ID must start with cam_.", {
        variant: "warning",
      });
      return;
    }
    logs.reset();
    setRunStartTime(new Date());
    mutation.mutate({
      max_contacts: maxContacts,
      icp_name: selectedIcp,
      campaign_id: campaignId,
      dry_run: dryRun,
      ignore_other_campaigns: ignoreOtherCampaigns,
      ignore_pipedrive_dedup: ignorePipedriveDedup,
    });
  };

  const { variant, message } = statusFromMutation(mutation);

  return (
    <section className="card">
      <h2>Contact Research</h2>
      <p className="card-desc">
        Pulls net-new contacts from Apollo, deduplicates against Pipedrive and
        Lemlist, crawls company websites, scores with AI, and pushes qualified
        leads to the selected Lemlist campaign. Max contacts is the number of
        pulled contacts from Apollo.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="cr-max">Max contacts</label>
          <input
            type="number"
            id="cr-max"
            min={1}
            value={maxContacts}
            onChange={(e) => setMaxContacts(parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <div className="field">
          <label htmlFor="cr-icp">ICP name</label>
          <select
            id="cr-icp"
            value={selectedIcp}
            onChange={(e) => {
              setIcpName(e.target.value);
              setCampaignId("");
            }}
          >
            {(icps ?? []).map((icp) => (
              <option key={icp.name} value={icp.name}>
                {icp.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="cr-campaign">Campaign ID</label>
        <CampaignInput
          id="cr-campaign"
          value={campaignId}
          onChange={setCampaignId}
          contextIcp={selectedIcp}
          required
        />
      </div>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={ignoreOtherCampaigns}
          onChange={(e) => setIgnoreOtherCampaigns(e.target.checked)}
        />
        Ignore other Lemlist campaigns (allow contacts to be in multiple
        campaigns at once)
      </label>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={ignorePipedriveDedup}
          onChange={(e) => setIgnorePipedriveDedup(e.target.checked)}
        />
        Ignore Pipedrive dedup (skip filtering out contacts already in
        Pipedrive)
      </label>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        Dry run
      </label>
      <button onClick={onRun} disabled={mutation.isPending}>
        Run
      </button>
      <StatusLine variant={variant} message={message} />
      <LogStream
        entries={logs.entries}
        isPolling={logs.isPolling}
        lastError={logs.lastError}
      />
      {mutation.isSuccess && <ContactResearchResult data={mutation.data} />}
      {mutation.isError && <JsonResult data={errorPayload(mutation.error)} />}
    </section>
  );
}

